import { ChevronDown, FileText } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatPriceCents } from "@/components/services/service-meta";

export type FichaResumo = {
  id: string;
  name: string;
  serviceCategoryId: string;
  materialOptionId: string | null;
  materialLabel: string | null;
  linhas: number;
  totalCents: number;
  emAberto: boolean;
  semPreco: number;
};

/** O contador de fichas, dentro da linha do serviço. Some quando o serviço não tem ficha nenhuma —
 *  um "0" em cada linha só faria a lista parecer mais cheia do que é. */
export function BotaoFichas({
  quantas,
  aberto,
  onAlternar,
  label,
}: {
  quantas: number;
  aberto: boolean;
  onAlternar: () => void;
  label: string;
}) {
  if (quantas === 0) return null;
  return (
    <button
      type="button"
      onClick={onAlternar}
      aria-expanded={aberto}
      aria-label={`${aberto ? "Ocultar" : "Ver"} fichas técnicas de ${label}`}
      title={aberto ? "Ocultar fichas técnicas" : "Ver fichas técnicas"}
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-[11px] font-semibold text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:text-muted-foreground dark:hover:bg-white/5 dark:hover:text-foreground"
    >
      <FileText className="h-3 w-3" />
      {quantas}
      <ChevronDown className={cn("h-3 w-3 transition-transform", aberto && "rotate-180")} />
    </button>
  );
}

/**
 * As fichas técnicas de um serviço, abertas na própria lista.
 *
 * Existe porque o custo de um serviço não mora em um número só: "Coroa anatômica" tem MC XL a
 * R$ 91,38 e XTCERA a R$ 64,68. Ver isso lado a lado é o que responde "qual receita está cara".
 */
export function ListaFichas({
  fichas,
  onAbrir,
}: {
  fichas: FichaResumo[];
  onAbrir: (ficha: FichaResumo) => void;
}) {
  return (
    <div className="mb-1 ml-8 space-y-0.5 border-l border-zinc-200 pl-2.5 dark:border-border">
      {fichas.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => onAbrir(f)}
          title={`Abrir a ficha "${f.name}"`}
          className="flex w-full items-baseline gap-2 rounded px-1.5 py-1 text-left hover:bg-zinc-50 dark:hover:bg-white/5"
        >
          <span className="truncate text-[11.5px] font-medium text-zinc-700 dark:text-foreground/80">{f.name}</span>
          <span className="truncate text-[10.5px] text-zinc-400 dark:text-muted-foreground/70">
            {f.materialLabel ?? "qualquer material"} · {f.linhas} {f.linhas === 1 ? "item" : "itens"}
          </span>
          <span className="ml-auto shrink-0 text-[11px] font-semibold tabular-nums text-zinc-600 dark:text-foreground/70">
            {formatPriceCents(f.totalCents)}
            {f.emAberto && <span className="ml-0.5 font-normal text-amber-600">+</span>}
            {f.semPreco > 0 && (
              <span title={`${f.semPreco} sem preço de compra`} className="ml-0.5 font-normal text-amber-600">
                !
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}
